// Enemy manager + the four standard enemy types (trooper, hornet, lancer, bulwark)
// with elite affixes. Every attack is telegraphed with shape + animation + colour
// through ctx.fx before it lands.

import Phaser from 'phaser';
import { TEX } from '../../art/keys';
import { DEPTH, FONT } from '../../config/constants';
import { COLORS, css } from '../../config/palette';
import type { EnemyApi, EnemyManagerApi, GameContext, HitArea, TelegraphHandle } from '../../core/contracts';
import type { DamageInfo, DamageResult, EliteAffix, EnemyId, StatusId, StatusState } from '../../core/types';
import {
  AFFIX,
  BULWARK_AI,
  ELITE_AFFIXES_DEF,
  ENEMIES,
  HORNET_AI,
  LANCER_AI,
  TROOPER_AI,
  computeEnemyStats,
  volatileBlastDamage,
  type EnemyDef,
  type EnemyStats,
} from '../../data/enemies';
import { ensureEnemyArt } from './art';
import { ENEMY_FX, RIGS, enemyAnimKey, frameMeta, frameName, outlineFrame, type EnemyAnim } from './rig';

let nextUid = 1;

type Phase = 'move' | 'windup' | 'attack' | 'recover' | 'stagger';

class Enemy implements EnemyApi {
  readonly uid = nextUid++;
  readonly kind = 'enemy' as const;
  readonly name: string;
  readonly statuses = new Map<StatusId, StatusState>();
  readonly obj: Phaser.GameObjects.Zone;
  readonly body: Phaser.Physics.Arcade.Body;
  readonly flying: boolean;
  readonly knockbackResist: number;
  readonly maxHp: number;
  readonly scrapValue: number;
  hp: number;
  armorHp: number;
  alive = true;
  spawning = false;
  dying = false;

  private sprite: Phaser.GameObjects.Sprite;
  private outline: Phaser.GameObjects.Sprite | null = null;
  private gun: Phaser.GameObjects.Image | null = null;
  private shield: Phaser.GameObjects.Image | null = null;
  private hex: Phaser.GameObjects.Image | null = null;
  private label: Phaser.GameObjects.Text | null = null;
  private barBg: Phaser.GameObjects.Rectangle;
  private bar: Phaser.GameObjects.Rectangle;
  private barTimer = 0;

  private facing: 1 | -1 = 1;
  private anim: EnemyAnim = 'idle';
  private phase: Phase = 'move';
  private phaseT = 0;
  private cooldown: number;
  private knockT = 0;
  private aimAngle = 0;
  private lockAngle = 0;
  private lockX = 0;
  private lockY = 0;
  private shots = 0;
  private shotT = 0;
  private tele: TelegraphHandle | null = null;
  private orbit = Math.random() * Math.PI * 2;
  private t = 0;
  private buzzT = 4;
  private dashDir = 1;
  private dashLeft = 0;
  private turnT = 0;
  private swiftT = 0;

  constructor(
    private readonly ctx: GameContext,
    private readonly mgr: EnemyManager,
    readonly defId: EnemyId,
    x: number,
    y: number,
    readonly elite: readonly EliteAffix[],
    private readonly stats: EnemyStats,
  ) {
    const def = ENEMIES[defId];
    const scene = ctx.scene;
    const rig = RIGS[defId];
    this.name = def.name;
    this.flying = def.flying;
    this.knockbackResist = def.knockbackResist;
    this.maxHp = stats.maxHp;
    this.hp = stats.maxHp;
    this.armorHp = stats.maxArmor;
    this.scrapValue = stats.scrap;
    this.cooldown = this.firstDelay();

    const w = def.body.w * stats.scale;
    const h = def.body.h * stats.scale;
    // Ground spawns are feet positions; flyers are centres.
    const cy = this.flying ? y : y - h / 2;
    this.obj = scene.add.zone(x, cy, w, h);
    scene.physics.add.existing(this.obj);
    this.body = this.obj.body as Phaser.Physics.Arcade.Body;
    this.body.setAllowGravity(true);
    this.body.setGravityY(this.flying ? 0 : def.gravity);
    this.body.setMaxVelocity(1400, 1600);

    const tex = scene.textures.exists(rig.key) ? rig.key : ENEMY_FX.placeholder;
    if (this.elite.length && scene.textures.exists(rig.key)) {
      this.outline = scene.add.sprite(x, y, rig.key, outlineFrame(frameName('idle', 0))).setDepth(DEPTH.ENEMIES - 0.1);
      this.outline.setOrigin(rig.anchorX / rig.cellW, rig.anchorY / rig.cellH).setTint(ELITE_AFFIXES_DEF[this.elite[0]].color);
      this.outline.setBlendMode(Phaser.BlendModes.ADD);
    }
    this.sprite = scene.add.sprite(x, y, tex, scene.textures.exists(rig.key) ? frameName('idle', 0) : undefined).setDepth(DEPTH.ENEMIES);
    if (tex === rig.key) this.sprite.setOrigin(rig.anchorX / rig.cellW, rig.anchorY / rig.cellH);
    if (rig.gun && scene.textures.get(rig.key).has('gun')) {
      this.gun = scene.add.image(x, y, rig.key, 'gun').setDepth(DEPTH.ENEMIES + 0.1);
      this.gun.setOrigin(rig.gun.pivotX / rig.gun.w, rig.gun.pivotY / rig.gun.h);
    }
    if (rig.shield && scene.textures.exists(rig.shield.key)) {
      this.shield = scene.add.image(x, y, rig.shield.key).setDepth(DEPTH.ENEMIES + 0.2);
      this.shield.setOrigin(rig.shield.gripX / rig.shield.w, rig.shield.gripY / rig.shield.h);
    }
    if (this.armorHp > 0 && scene.textures.exists(ENEMY_FX.hex)) {
      this.hex = scene.add.image(x, cy, ENEMY_FX.hex).setDepth(DEPTH.ENEMIES + 0.3).setTint(COLORS.shield).setBlendMode(Phaser.BlendModes.ADD);
    }
    if (this.elite.length) {
      const names = this.elite.map((a) => ELITE_AFFIXES_DEF[a].name).join(' · ');
      this.label = scene.add
        .text(x, y, `ELITE  ${names}`, { fontFamily: FONT.display, fontSize: '15px', fontStyle: '700', color: css(ELITE_AFFIXES_DEF[this.elite[0]].color), stroke: '#05070b', strokeThickness: 4 })
        .setOrigin(0.5, 1)
        .setDepth(DEPTH.WORLD_UI - 1);
    }
    this.barBg = scene.add.rectangle(x, y, 44, 5, 0x05070b, 0.8).setDepth(DEPTH.WORLD_UI - 2).setVisible(false);
    this.bar = scene.add.rectangle(x, y, 44, 3, COLORS.threat).setOrigin(0, 0.5).setDepth(DEPTH.WORLD_UI - 2).setVisible(false);
    this.play('idle');
  }

  get x(): number {
    return this.body.center.x;
  }
  get y(): number {
    return this.body.center.y;
  }
  get radius(): number {
    return Math.max(this.body.width, this.body.height) * 0.5;
  }
  private get def(): EnemyDef {
    return ENEMIES[this.defId];
  }
  private get feetY(): number {
    return this.body.bottom;
  }

  private firstDelay(): number {
    const r = { trooper: TROOPER_AI.firstShotDelay, hornet: HORNET_AI.firstDiveDelay, lancer: LANCER_AI.firstShotDelay, bulwark: BULWARK_AI.firstBashDelay }[this.defId];
    return this.ctx.rng.float(r[0], r[1]);
  }

  setSpawning(on: boolean): void {
    this.spawning = on;
    this.body.enable = !on;
    for (const o of this.visuals()) o.setVisible(!on);
    if (!on) this.barBg.setVisible(false), this.bar.setVisible(false);
  }

  private visuals(): (Phaser.GameObjects.Components.Visible & Phaser.GameObjects.GameObject)[] {
    return [this.sprite, this.outline, this.gun, this.shield, this.hex, this.label].filter(Boolean) as never[];
  }

  hitAreas(): readonly HitArea[] {
    const b = this.body;
    return [{ x: b.x, y: b.y, w: b.width, h: b.height, mult: 1 }];
  }

  /** Bulwark frontal shield: blocks shots from its facing side while raised. */
  blocks(fromX: number, fromY: number): boolean {
    if (this.defId !== 'bulwark' || !this.alive || this.phase === 'recover' || this.phase === 'stagger') return false;
    const front = (fromX - this.x) * this.facing > 0;
    const notAbove = fromY > this.body.top - 20;
    return front && notAbove;
  }

  receiveDamage(amount: number, info: DamageInfo): DamageResult {
    if (!this.alive || this.spawning) return { dealt: 0, killed: false, blocked: true, absorbed: 0 };
    let rest = amount;
    let absorbed = 0;
    if (this.armorHp > 0) {
      absorbed = Math.min(this.armorHp, rest);
      this.armorHp -= absorbed;
      rest -= absorbed;
      if (this.armorHp <= 0 && this.hex) {
        this.ctx.fx.ring(this.x, this.y, 70, { color: COLORS.shield, duration: 260 });
        this.hex.destroy();
        this.hex = null;
      }
    }
    this.hp -= rest;
    this.barTimer = 2.5;
    if (!info.isDot) {
      this.ctx.fx.hitFlash(this.sprite, 60);
      // Heavy hits interrupt a wind-up (poise).
      if (amount >= this.maxHp * this.stats.poise && this.phase !== 'attack' && this.defId !== 'bulwark') this.enter('stagger');
    }
    const killed = this.hp <= 0;
    if (killed) this.die();
    return { dealt: amount, killed, blocked: false, absorbed };
  }

  knockback(vx: number, vy: number): void {
    if (!this.alive) return;
    const k = 1 - this.knockbackResist;
    this.body.velocity.x += vx * k;
    this.body.velocity.y += vy * k * (this.flying ? 1 : 0.6);
    this.knockT = 0.18 * k;
  }

  pull(ax: number, ay: number, dt: number): void {
    if (!this.alive) return;
    const k = 1 - this.knockbackResist * 0.6;
    this.body.velocity.x += ax * dt * k;
    this.body.velocity.y += ay * dt * k;
    this.knockT = Math.max(this.knockT, 0.05);
  }

  private die(): void {
    if (this.dying) return;
    this.alive = false;
    this.dying = true;
    this.tele?.cancel();
    const { ctx } = this;
    const x = this.x;
    const y = this.y;
    ctx.fx.explosion(x, y, 46 * this.stats.scale, COLORS.threatHot);
    ctx.fx.debris(x, y, { count: 6, color: COLORS.steel3 });
    ctx.fx.sparks(x, y, -Math.PI / 2, { count: 10, color: COLORS.threatHot, spread: Math.PI });
    ctx.audio.play('enemy_death', { volume: this.elite.length ? 1 : 0.8 });
    this.body.enable = false;
    if (this.elite.includes('volatile')) {
      const r = AFFIX.volatile.radius;
      ctx.fx.warnCircle(x, y, r, AFFIX.volatile.fuseMs, { color: COLORS.threatHot });
      ctx.scene.time.delayedCall(AFFIX.volatile.fuseMs, () => {
        if (!ctx.scene.sys.isActive()) return;
        ctx.damage.explode(x, y, r, { amount: volatileBlastDamage(this.stats.power), tags: ['explosive'], source: 'enemy', knockback: AFFIX.volatile.knockback, label: 'volatile' }, { hurtsPlayer: true, color: COLORS.threatHot });
      });
    }
    ctx.scene.tweens.add({
      targets: this.visuals(),
      alpha: 0,
      duration: 260,
      onComplete: () => this.destroy(),
    });
    this.barBg.setVisible(false);
    this.bar.setVisible(false);
    this.mgr.unregister(this);
  }

  destroy(): void {
    this.tele?.cancel();
    for (const o of [this.sprite, this.outline, this.gun, this.shield, this.hex, this.label, this.barBg, this.bar, this.obj]) o?.destroy();
  }

  // ───────────── AI ─────────────

  private status(id: StatusId): StatusState | undefined {
    const s = this.statuses.get(id);
    return s && s.remaining > 0 ? s : undefined;
  }

  private speed(): number {
    const slow = this.status('slow');
    return this.stats.speed * (slow ? Math.max(0.15, 1 - slow.potency) : 1);
  }

  private enter(phase: Phase, t = 0): void {
    this.phase = phase;
    this.phaseT = t;
    if (phase !== 'windup') {
      this.tele?.cancel();
      this.tele = null;
    }
    if (phase === 'stagger') this.phaseT = 0.35;
  }

  private play(anim: EnemyAnim): void {
    const key = enemyAnimKey(this.defId, anim);
    if (this.anim === anim && this.sprite.anims.isPlaying) return;
    this.anim = anim;
    if (this.ctx.scene.anims.exists(key)) this.sprite.play(key, true);
  }

  private canSee(): boolean {
    const p = this.ctx.player;
    const hit = this.ctx.world.raycast(this.x, this.y - 10, p.x, p.y);
    return !hit.hit;
  }

  private muzzle(): { x: number; y: number } {
    const rig = RIGS[this.defId];
    if (!this.gun || !rig.gun) return { x: this.x + this.facing * 20, y: this.y - 8 };
    const r = this.gun.rotation;
    const sx = this.gun.scaleX;
    const mx = rig.gun.muzzleX * sx;
    const my = rig.gun.muzzleY * this.gun.scaleY;
    return { x: this.gun.x + Math.cos(r) * mx - Math.sin(r) * my, y: this.gun.y + Math.sin(r) * mx + Math.cos(r) * my };
  }

  update(dt: number): void {
    if (!this.alive || this.spawning) return;
    this.t += dt;
    const p = this.ctx.player;
    const stunned = !!this.status('stun');
    if (this.knockT > 0) this.knockT -= dt;

    if (stunned) {
      this.tele?.cancel();
      this.tele = null;
      if (!this.flying) this.body.velocity.x *= 0.85;
      else this.body.velocity.scale(0.9);
      this.play('stagger');
    } else if (p && p.alive) {
      switch (this.defId) {
        case 'trooper':
          this.aiShooter(dt, TROOPER_AI.preferredRange, TROOPER_AI.rangeSlack, TROOPER_AI.fireRange);
          break;
        case 'lancer':
          this.aiShooter(dt, LANCER_AI.idealRange, 200, LANCER_AI.fireRange);
          break;
        case 'hornet':
          this.aiHornet(dt);
          break;
        case 'bulwark':
          this.aiBulwark(dt);
          break;
      }
    } else if (!this.flying) {
      this.body.velocity.x *= 0.8;
      this.play('idle');
    }
    this.syncVisuals(dt);
  }

  private face(x: number): void {
    if (Math.abs(x - this.x) > 6) this.facing = x < this.x ? -1 : 1;
  }

  /** Walk toward `dir` (-1/0/1) without walking off ledges; hop small steps. */
  private walk(dir: number, dt: number): void {
    if (this.knockT > 0) return;
    const b = this.body;
    let target = dir * this.speed();
    if (dir !== 0) {
      const aheadX = this.x + dir * (b.width / 2 + 10);
      const w = this.ctx.world;
      const ground = w.isSolidAt(aheadX, this.feetY + 12) || w.isOneWayAt(aheadX, this.feetY + 12);
      const below = ground ? this.feetY : w.groundBelow(aheadX, this.feetY + 12, TROOPER_AI.maxDrop);
      if (below === null) target = 0; // ledge too high to drop
      const wall = w.isSolidAt(aheadX, this.feetY - 24);
      if (wall && b.blocked.down) {
        const tall = w.isSolidAt(aheadX, this.feetY - TROOPER_AI.maxStep - 24);
        if (!tall) b.setVelocityY(-TROOPER_AI.hopVelocity * 0.85);
        else target = 0;
      }
    }
    const k = 1 - Math.exp(-dt * 10);
    b.velocity.x += (target - b.velocity.x) * k;
    this.play(Math.abs(b.velocity.x) > 20 ? 'move' : 'idle');
  }

  /** Trooper and lancer: hold a preferred range with line of sight, telegraph, fire. */
  private aiShooter(dt: number, range: number, slack: number, fireRange: number): void {
    const p = this.ctx.player;
    const dx = p.x - this.x;
    const dist = Math.hypot(dx, p.y - this.y);
    const atk = this.def.attack;
    const isLancer = this.defId === 'lancer';
    const turnRate = isLancer ? LANCER_AI.aimTurnRate : TROOPER_AI.aimTurnRate;
    const desired = Math.atan2(p.y - this.y, p.x - this.x);

    if (this.phase === 'move' || this.phase === 'recover') {
      this.face(p.x);
      let dir = 0;
      if (isLancer && dist < LANCER_AI.retreatRange) dir = -Math.sign(dx);
      else if (dist > range + slack) dir = Math.sign(dx);
      else if (dist < range - slack) dir = -Math.sign(dx);
      this.walk(dir, dt);
      this.aimAngle = rotateToward(this.aimAngle, desired, turnRate * dt);
      this.cooldown -= dt * this.stats.attackRate;
      if (this.phase === 'recover') {
        this.phaseT -= dt;
        if (this.phaseT <= 0) this.enter('move');
      } else if (this.cooldown <= 0 && dist < fireRange && this.canSee()) {
        this.enter('windup', atk.windup);
        const m = this.muzzle();
        this.tele = this.ctx.fx.warnLine(m.x, m.y, p.x, p.y, atk.windup * 1000, { width: isLancer ? 5 : 4, color: isLancer ? COLORS.threat : COLORS.telegraph });
        this.ctx.fx.warnMarker(this.x, this.body.top - 18, atk.windup * 1000, { follow: this.obj });
        this.ctx.audio.play(isLancer ? 'enemy_laser_charge' : 'enemy_telegraph', { volume: 0.7 });
      }
      return;
    }

    if (this.phase === 'windup') {
      this.walk(0, dt);
      this.play('windup');
      this.phaseT -= dt;
      const locked = this.phaseT <= atk.lock;
      if (!locked) {
        this.face(p.x);
        this.aimAngle = rotateToward(this.aimAngle, desired, turnRate * 1.5 * dt);
        this.lockX = p.x;
        this.lockY = p.y;
      }
      const m = this.muzzle();
      const len = isLancer ? 1600 : Math.max(200, Math.hypot(this.lockX - m.x, this.lockY - m.y));
      this.tele?.update({ x: m.x, y: m.y, x2: m.x + Math.cos(this.aimAngle) * len, y2: m.y + Math.sin(this.aimAngle) * len });
      if (this.phaseT <= 0) {
        this.lockAngle = this.aimAngle;
        this.shots = atk.count + this.stats.extraShots;
        this.shotT = 0;
        this.enter('attack');
      }
      return;
    }

    if (this.phase === 'attack') {
      this.walk(0, dt);
      this.play('attack');
      this.shotT -= dt;
      if (this.shotT <= 0 && this.shots > 0) {
        this.shots--;
        this.shotT = atk.interval;
        const m = this.muzzle();
        const spread = isLancer ? 0 : this.ctx.rng.float(-0.04, 0.04);
        this.ctx.projectiles.spawn({
          owner: 'enemy',
          x: m.x,
          y: m.y,
          angle: this.lockAngle + spread,
          speed: atk.projectileSpeed,
          damage: this.stats.attackDamage,
          tags: [],
          texture: isLancer ? TEX.projEnemyNeedle : TEX.projEnemyBolt,
          radius: isLancer ? LANCER_AI.needleRadius : TROOPER_AI.boltRadius,
          lifetime: isLancer ? 1.4 : 2.2,
          glow: true,
          trail: 'enemy',
          knockback: isLancer ? 420 : 220,
        });
        this.ctx.fx.muzzleFlash(m.x, m.y, this.lockAngle, COLORS.threat, isLancer ? 1.2 : 0.8);
        this.ctx.audio.play(isLancer ? 'enemy_laser_fire' : 'enemy_shoot', { volume: 0.6 });
      }
      if (this.shots <= 0 && this.shotT <= 0) {
        this.cooldown = this.ctx.rng.float(atk.cooldown[0], atk.cooldown[1]);
        this.enter('recover', atk.recover);
      }
      return;
    }

    if (this.phase === 'stagger') {
      this.walk(0, dt);
      this.play('stagger');
      this.phaseT -= dt;
      if (this.phaseT <= 0) this.enter('move');
    }
  }

  /** Hornet: orbit, pull back, dive through a locked point, recover upward. */
  private aiHornet(dt: number): void {
    const p = this.ctx.player;
    const b = this.body;
    const atk = this.def.attack;
    this.buzzT -= dt;
    if (this.buzzT <= 0) {
      this.buzzT = this.ctx.rng.float(HORNET_AI.buzzInterval[0], HORNET_AI.buzzInterval[1]);
      this.ctx.audio.play('drone_buzz', { volume: 0.35 });
    }
    const steer = (tx: number, ty: number, maxSp: number) => {
      const dx = tx - this.x;
      const dy = ty - this.y;
      const d = Math.hypot(dx, dy) || 1;
      const want = Math.min(maxSp, d * 4);
      const vx = (dx / d) * want;
      const vy = (dy / d) * want;
      const k = Math.min(1, (HORNET_AI.accel * dt) / Math.max(1, Math.hypot(vx - b.velocity.x, vy - b.velocity.y)));
      b.velocity.x += (vx - b.velocity.x) * k;
      b.velocity.y += (vy - b.velocity.y) * k;
    };

    if (this.phase === 'move' || this.phase === 'recover') {
      this.orbit += HORNET_AI.orbitDrift * dt;
      const r = (HORNET_AI.orbitMin + HORNET_AI.orbitMax) / 2 + Math.sin(this.t * 0.7) * 30;
      const tx = p.x + Math.cos(this.orbit) * r;
      const ty = p.y - 160 + Math.sin(this.orbit * 1.3) * 60 + Math.sin(this.t * HORNET_AI.bobFreq) * HORNET_AI.bobAmp;
      if (this.knockT <= 0) steer(tx, ty, this.speed());
      this.face(p.x);
      this.play('move');
      if (this.phase === 'recover') {
        this.phaseT -= dt;
        if (this.phaseT <= 0) this.enter('move');
      }
      this.cooldown -= dt * this.stats.attackRate;
      if (this.phase === 'move' && this.cooldown <= 0 && Math.hypot(p.x - this.x, p.y - this.y) < 620 && this.canSee()) {
        this.lockX = p.x;
        this.lockY = p.y;
        this.enter('windup', atk.windup);
        const ang = Math.atan2(this.lockY - this.y, this.lockX - this.x);
        const len = Math.hypot(this.lockX - this.x, this.lockY - this.y) + HORNET_AI.overshoot;
        this.tele = this.ctx.fx.warnLine(this.x, this.y, this.x + Math.cos(ang) * len, this.y + Math.sin(ang) * len, atk.windup * 1000, { width: 8 });
        this.ctx.audio.play('enemy_telegraph', { volume: 0.6, pitch: 1.3 });
      }
      return;
    }

    if (this.phase === 'windup') {
      const ang = Math.atan2(this.lockY - this.y, this.lockX - this.x);
      b.setVelocity(-Math.cos(ang) * HORNET_AI.pullBackSpeed, -Math.sin(ang) * HORNET_AI.pullBackSpeed);
      this.face(this.lockX);
      this.play('windup');
      const len = Math.hypot(this.lockX - this.x, this.lockY - this.y) + HORNET_AI.overshoot;
      this.tele?.update({ x: this.x, y: this.y, x2: this.x + Math.cos(ang) * len, y2: this.y + Math.sin(ang) * len });
      this.phaseT -= dt;
      if (this.phaseT <= 0) {
        this.lockAngle = ang;
        this.dashLeft = len;
        this.enter('attack');
        b.setVelocity(Math.cos(ang) * atk.projectileSpeed, Math.sin(ang) * atk.projectileSpeed);
      }
      return;
    }

    if (this.phase === 'attack') {
      this.play('attack');
      this.dashLeft -= atk.projectileSpeed * dt;
      b.setVelocity(Math.cos(this.lockAngle) * atk.projectileSpeed, Math.sin(this.lockAngle) * atk.projectileSpeed);
      const bonk = b.blocked.left || b.blocked.right || b.blocked.down || b.blocked.up;
      if (bonk) {
        this.ctx.fx.sparks(this.x, this.y, this.lockAngle + Math.PI, { count: 8 });
        this.enter('stagger');
        this.phaseT = HORNET_AI.bonkStun;
      } else if (this.dashLeft <= 0) {
        this.cooldown = this.ctx.rng.float(atk.cooldown[0], atk.cooldown[1]);
        this.enter('recover', atk.recover);
        b.setVelocity(b.velocity.x * 0.3, -HORNET_AI.recoverRise);
      }
      return;
    }

    if (this.phase === 'stagger') {
      b.velocity.scale(0.9);
      b.velocity.y += 300 * dt;
      this.play('stagger');
      this.phaseT -= dt;
      if (this.phaseT <= 0) {
        this.cooldown = this.ctx.rng.float(atk.cooldown[0], atk.cooldown[1]);
        this.enter('recover', atk.recover);
      }
    }
  }

  /** Bulwark: advance behind the shield, telegraphed bash, vent with the shield down. */
  private aiBulwark(dt: number): void {
    const p = this.ctx.player;
    const atk = this.def.attack;
    const dx = p.x - this.x;
    const dy = p.y - this.y;

    if (this.phase === 'move') {
      // Turn toward the player only after they stay behind for a moment.
      if (Math.sign(dx) !== this.facing && Math.abs(dx) > 30) {
        this.turnT += dt;
        if (this.turnT > BULWARK_AI.turnDelay) {
          this.facing = this.facing === 1 ? -1 : 1;
          this.turnT = 0;
        }
      } else this.turnT = 0;
      this.walk(Math.abs(dx) > 90 ? this.facing : 0, dt);
      this.cooldown -= dt * this.stats.attackRate;
      const inFront = dx * this.facing > BULWARK_AI.bashMinRange && dx * this.facing < BULWARK_AI.bashMaxRange;
      if (this.cooldown <= 0 && inFront && Math.abs(dy) < BULWARK_AI.bashMaxDy && this.body.blocked.down) {
        this.enter('windup', atk.windup);
        const x0 = this.facing > 0 ? this.x : this.x - BULWARK_AI.bashDistance;
        this.tele = this.ctx.fx.warnRect(x0, this.feetY - this.body.height, BULWARK_AI.bashDistance, this.body.height, atk.windup * 1000);
        this.ctx.audio.play('enemy_telegraph', { volume: 0.8, pitch: 0.7 });
      }
      return;
    }
    if (this.phase === 'windup') {
      this.walk(0, dt);
      this.play('windup');
      this.phaseT -= dt;
      if (this.phaseT <= 0) {
        this.dashDir = this.facing;
        this.dashLeft = BULWARK_AI.bashDistance;
        this.enter('attack');
      }
      return;
    }
    if (this.phase === 'attack') {
      this.play('attack');
      const step = BULWARK_AI.bashSpeed * dt;
      this.dashLeft -= step;
      this.body.setVelocityX(this.dashDir * BULWARK_AI.bashSpeed);
      if (this.dashLeft <= 0 || this.body.blocked.left || this.body.blocked.right) {
        this.ctx.fx.shake(0.004, 120);
        this.ctx.fx.smoke(this.x + this.dashDir * 30, this.feetY - 10, { count: 4 });
        this.body.setVelocityX(0);
        this.cooldown = this.ctx.rng.float(atk.cooldown[0], atk.cooldown[1]);
        this.enter('recover', atk.recover);
      }
      return;
    }
    // recover / stagger: shield down, vulnerable.
    this.walk(0, dt);
    this.play('stagger');
    this.phaseT -= dt;
    if (this.phaseT <= 0) this.enter('move');
  }

  /** Damage dealt by touching this enemy right now (bash hits harder). */
  contactDamage(): number {
    if (this.defId === 'bulwark' && this.phase === 'attack') return this.stats.attackDamage;
    if (this.defId === 'hornet' && this.phase === 'attack') return this.stats.attackDamage;
    return this.stats.contactDamage;
  }

  contactKnockback(): number {
    return this.defId === 'bulwark' && this.phase === 'attack' ? BULWARK_AI.bashKnockback : 520;
  }

  private syncVisuals(dt: number): void {
    const rig = RIGS[this.defId];
    const s = this.stats.scale;
    const fx = this.flying ? this.x : this.x;
    const fy = this.flying ? this.y : this.feetY;
    this.sprite.setPosition(fx, fy).setScale(this.facing * s, s);
    const over = this.status('overheated');
    if (over) this.sprite.setTint(0xffb080);
    else if (this.sprite.isTinted && !this.sprite.tintFill) this.sprite.clearTint();
    if (this.outline) {
      const name = outlineFrame(String(this.sprite.frame.name));
      if (this.outline.texture.has(name)) this.outline.setFrame(name);
      this.outline.setPosition(fx, fy).setScale(this.facing * s, s).setAlpha(0.55 + 0.35 * Math.sin(this.t * 6));
    }
    const idx = Math.max(0, (this.sprite.anims.currentFrame?.index ?? 1) - 1);
    const meta = this.ctx.scene.textures.exists(rig.key) ? frameMeta(this.defId, this.anim, idx) : null;
    if (this.gun && meta && rig.gun) {
      this.gun.setPosition(fx + meta.pivot.x * s * this.facing, fy + meta.pivot.y * s);
      // Aim in local (right-facing) space, clamped to the rig limits.
      const local = this.facing > 0 ? this.aimAngle : Math.PI - this.aimAngle;
      const clamped = Phaser.Math.Clamp(Phaser.Math.Angle.Wrap(local), rig.gun.minAngle, rig.gun.maxAngle);
      const aiming = this.phase !== 'move' || this.defId === 'lancer' || this.defId === 'trooper';
      const a = aiming ? clamped : rig.gun.restAngle;
      this.gun.setScale(this.facing * s, s).setRotation(this.facing > 0 ? a : -a);
    }
    if (this.shield && meta) {
      const down = this.phase === 'recover' || this.phase === 'stagger';
      this.shield.setPosition(fx + meta.pivot.x * s * this.facing, fy + meta.pivot.y * s + (down ? 26 : 0));
      this.shield.setScale(this.facing * s, s).setRotation(down ? this.facing * 0.5 : 0).setAlpha(down ? 0.45 : 1);
    }
    if (this.hex) this.hex.setPosition(this.x, this.y).setAlpha(0.25 + 0.5 * (this.armorHp / Math.max(1, this.stats.maxArmor)));
    const top = this.body.top - 10;
    this.label?.setPosition(this.x, top - 10);
    if (this.elite.includes('swift')) {
      this.swiftT -= dt;
      if (this.swiftT <= 0 && Math.abs(this.body.velocity.x) > 80) {
        this.swiftT = 0.08;
        this.ctx.fx.afterimage(this.sprite, { color: ELITE_AFFIXES_DEF.swift.color, alpha: 0.35, duration: 200 });
      }
    }
    this.barTimer -= dt;
    const showBar = this.barTimer > 0 && this.hp < this.maxHp;
    this.barBg.setVisible(showBar).setPosition(this.x, top);
    this.bar.setVisible(showBar).setPosition(this.x - 22, top).setSize(44 * Math.max(0, this.hp / this.maxHp), 3);
  }
}

function rotateToward(cur: number, target: number, maxStep: number): number {
  const d = Phaser.Math.Angle.Wrap(target - cur);
  return cur + Phaser.Math.Clamp(d, -maxStep, maxStep);
}

class EnemyManager implements EnemyManagerApi {
  readonly group: Phaser.Physics.Arcade.Group;
  private items: EnemyApi[] = [];
  private own = new Set<Enemy>();
  private pending: { e: Enemy; t: number }[] = [];
  private contactCd = new Map<number, number>();

  constructor(private readonly ctx: GameContext) {
    ensureEnemyArt(ctx.scene);
    this.group = ctx.scene.physics.add.group({ allowGravity: false });
  }

  get list(): readonly EnemyApi[] {
    return this.items;
  }

  spawn(id: EnemyId, x: number, y: number, opts: { elite?: EliteAffix[]; portalMs?: number; level?: number } = {}): EnemyApi {
    const ctx = this.ctx;
    const def = ENEMIES[id] ?? ENEMIES.trooper;
    const stats = computeEnemyStats(def, { difficulty: ctx.world?.sector?.difficulty ?? 1, level: opts.level ?? 0, elite: opts.elite ?? [] });
    const e = new Enemy(ctx, this, def.id, x, y, opts.elite ?? [], stats);
    this.own.add(e);
    this.items.push(e);
    this.group.add(e.obj);
    // Group membership resets body settings: reapply gravity.
    e.body.setAllowGravity(!def.flying);
    e.body.setGravityY(def.flying ? 0 : def.gravity);
    try {
      ctx.world.addCollider(e.obj);
    } catch {
      /* world not ready (sandbox) */
    }
    if (opts.portalMs && opts.portalMs > 0) {
      e.setSpawning(true);
      ctx.fx.spawnPortal(x, def.flying ? y : y - def.body.h / 2, opts.portalMs, { radius: Math.max(def.body.w, def.body.h) * 0.8 });
      ctx.audio.play('enemy_spawn', { volume: 0.5 });
      this.pending.push({ e, t: opts.portalMs / 1000 });
    } else {
      ctx.bus.emit('enemy:spawned', { enemy: e });
    }
    return e;
  }

  register(e: EnemyApi): void {
    if (!this.items.includes(e)) this.items.push(e);
    const body = e.obj as Phaser.GameObjects.GameObject & { body?: unknown };
    if (body.body && !this.group.contains(e.obj)) {
      this.group.add(e.obj);
    }
  }

  unregister(e: EnemyApi): void {
    const i = this.items.indexOf(e);
    if (i >= 0) this.items.splice(i, 1);
    if (this.group.contains(e.obj)) this.group.remove(e.obj, false, false);
  }

  nearest(x: number, y: number, maxDist = Infinity, exclude?: Set<EnemyApi>): EnemyApi | null {
    let best: EnemyApi | null = null;
    let bd = maxDist;
    for (const e of this.items) {
      if (!e.alive || e.spawning || e.kind === 'prop' || exclude?.has(e)) continue;
      const d = Math.hypot(e.x - x, e.y - y);
      if (d < bd) {
        bd = d;
        best = e;
      }
    }
    return best;
  }

  inRadius(x: number, y: number, r: number, includeProps = false): EnemyApi[] {
    return this.items.filter((e) => e.alive && !e.spawning && (includeProps || e.kind !== 'prop') && Math.hypot(e.x - x, e.y - y) <= r + e.radius);
  }

  aliveCount(): number {
    let n = 0;
    for (const e of this.items) if (e.alive && e.kind !== 'prop') n++;
    return n;
  }

  killAll(): void {
    for (const e of [...this.items]) {
      if (e.kind === 'prop' || !e.alive) continue;
      if (e instanceof Enemy && e.spawning) e.setSpawning(false);
      this.ctx.damage.hitEnemy(e, { amount: e.hp + e.armorHp + 1, tags: [], source: 'player', label: 'debug' });
    }
  }

  update(dt: number): void {
    for (let i = this.pending.length - 1; i >= 0; i--) {
      const p = this.pending[i];
      p.t -= dt;
      if (p.t <= 0) {
        this.pending.splice(i, 1);
        if (!p.e.alive) continue;
        p.e.setSpawning(false);
        this.ctx.bus.emit('enemy:spawned', { enemy: p.e });
      }
    }
    for (const e of this.own) {
      if (e.dying && !e.obj.active) this.own.delete(e);
      else e.update(dt);
    }
    // Drop entries that died outside the damage pipeline (props, bosses).
    for (let i = this.items.length - 1; i >= 0; i--) if (!this.items[i].alive) this.items.splice(i, 1);
    this.contactDamage(dt);
  }

  private contactDamage(dt: number): void {
    const p = this.ctx.player;
    if (!p || !p.alive) return;
    for (const [k, v] of this.contactCd) this.contactCd.set(k, v - dt);
    const pb = p.body;
    for (const e of this.own) {
      if (!e.alive || e.spawning) continue;
      if ((this.contactCd.get(e.uid) ?? 0) > 0) continue;
      if (e.statuses.get('stun')?.remaining) continue;
      const b = e.body;
      if (b.right < pb.left || b.left > pb.right || b.bottom < pb.top || b.top > pb.bottom) continue;
      const res = this.ctx.damage.hitPlayer({
        amount: e.contactDamage(),
        tags: [],
        source: 'enemy',
        sourceX: e.x,
        sourceY: e.y,
        knockback: e.contactKnockback(),
        label: 'contact',
      });
      if (!res.blocked) this.contactCd.set(e.uid, 0.5);
    }
  }

  destroy(): void {
    for (const e of this.own) e.destroy();
    this.own.clear();
    this.items = [];
    this.pending = [];
  }
}

export function createEnemies(ctx: GameContext): EnemyManagerApi {
  return new EnemyManager(ctx);
}
